import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import AnnouncementManager from "@/components/AnnouncementManager";
import { ChevronDown, ChevronRight } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ submission_type: "" });
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.submission_type) params.submission_type = filter.submission_type;
      const [d, s] = await Promise.all([
        api.get("/deals", { params }),
        api.get("/admin/stats"),
      ]);
      setDeals(d.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Active deals: not closed; unread first, then soonest closing date, then newest
  const { active, closed } = useMemo(() => {
    const a = [];
    const c = [];
    for (const d of deals) {
      if (d.status === "closed") c.push(d);
      else a.push(d);
    }
    a.sort((x, y) => {
      if (x.is_unread && !y.is_unread) return -1;
      if (!x.is_unread && y.is_unread) return 1;
      if (x.closing_date && y.closing_date) return x.closing_date.localeCompare(y.closing_date);
      if (x.closing_date && !y.closing_date) return -1;
      if (!x.closing_date && y.closing_date) return 1;
      return (y.created_at || "").localeCompare(x.created_at || "");
    });
    c.sort((x, y) => (y.created_at || "").localeCompare(x.created_at || ""));
    return { active: a, closed: c };
  }, [deals]);

  return (
    <div data-testid="admin-page">
      <div className="mb-10">
        <div className="section-tag mb-3"><span className="dot" /> Admin command</div>
        <h1 className="h-display text-4xl">Deal pipeline</h1>
      </div>

      <AnnouncementManager />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-10" data-testid="admin-stats">
          <StatCard label="Unread" val={stats.unread} accent={stats.unread > 0} pulse={stats.unread > 0} />
          <StatCard label="Total" val={stats.total} />
          <StatCard label="New" val={stats.new} />
          <StatCard label="Reviewing" val={stats.reviewing} />
          <StatCard label="Offered" val={stats.offered} accent />
          <StatCard label="JV Deals" val={stats.jv} />
          <StatCard label="Analyses" val={stats.help} />
        </div>
      )}

      <div className="surface-card p-4 mb-6 flex flex-wrap gap-3 items-center">
        <select
          className="input-base py-2 text-sm w-auto"
          value={filter.submission_type}
          onChange={(e) => setFilter({ ...filter, submission_type: e.target.value })}
          data-testid="filter-type"
        >
          <option value="">All types</option>
          <option value="help">Property Analysis</option>
          <option value="jv">JV Deal</option>
        </select>
        <button onClick={load} className="btn-ghost text-sm" data-testid="refresh-button">Refresh</button>
      </div>

      {loading ? (
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading...</div>
      ) : (
        <>
          {/* Active section */}
          <div className="flex items-end justify-between mb-4">
            <h2 className="h-display text-2xl">Active</h2>
            <span className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
              {active.length} {active.length === 1 ? "deal" : "deals"}
            </span>
          </div>

          {active.length === 0 ? (
            <div className="surface-card p-10 text-center mb-10">
              <p className="text-lg">No active deals.</p>
              <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                Submissions land here as soon as students send them in.
              </p>
            </div>
          ) : (
            <DealTable rows={active} onRowClick={(id) => navigate(`/deals/${id}`)} testidPrefix="admin-row" />
          )}

          {/* Closed section */}
          {closed.length > 0 && (
            <div className="mt-12">
              <button
                type="button"
                onClick={() => setShowClosed((v) => !v)}
                className="flex items-center gap-2 mb-4 group"
                data-testid="closed-toggle"
              >
                {showClosed ? <ChevronDown size={18} style={{ color: "var(--text-muted)" }} /> : <ChevronRight size={18} style={{ color: "var(--text-muted)" }} />}
                <h2 className="h-display text-2xl group-hover:opacity-80 transition">Closed</h2>
                <span className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                  ({closed.length})
                </span>
              </button>

              {showClosed && (
                <div className="opacity-70">
                  <DealTable rows={closed} onRowClick={(id) => navigate(`/deals/${id}`)} testidPrefix="admin-closed-row" />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function closingBadge(d) {
  if (!d.closing_date || d.status === "closed") return null;
  const days = Math.ceil((new Date(d.closing_date + "T00:00:00") - new Date(new Date().toDateString())) / 86400000);
  const label = days < 0 ? `Past close ${Math.abs(days)}d` : days === 0 ? "Closes today" : `Closes in ${days}d`;
  const color = days <= 3 ? "var(--red)" : days <= 7 ? "#ff8c32" : "var(--lime)";
  return (
    <span
      className="font-mono text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ color, border: `1px solid ${color}`, opacity: 0.9 }}
      data-testid={`closing-badge-${d.id}`}
    >
      {label}
    </span>
  );
}

function DealTable({ rows, onRowClick, testidPrefix }) {
  return (
    <div className="surface-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="wu-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Student</th>
              <th>Type</th>
              <th>ARV</th>
              <th>Asking</th>
              <th>Repairs</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr
                key={d.id}
                onClick={() => onRowClick(d.id)}
                data-testid={`${testidPrefix}-${d.id}`}
                style={{ background: d.is_unread ? "rgba(197,226,106,0.04)" : undefined }}
              >
                <td className="font-medium">
                  <div className="flex items-center gap-2">
                    {d.is_unread && (
                      <span
                        title="Unread"
                        data-testid={`unread-dot-${d.id}`}
                        style={{
                          width: 8, height: 8, borderRadius: 999,
                          background: "var(--lime)",
                          boxShadow: "0 0 8px rgba(197,226,106,0.7)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {d.property_address}
                    {closingBadge(d)}
                  </div>
                </td>
                <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                  {d.user_name || d.user_email}
                </td>
                <td className="font-mono text-xs">
                  <span style={{ color: d.submission_type === "jv" ? "var(--lime)" : "var(--blue)" }}>
                    {d.submission_type === "jv" ? "JV" : "ANALYZE"}
                  </span>
                  {d.ai_triage && <TriageBadge triage={d.ai_triage} />}
                </td>
                <td className="font-mono">{fmtMoney(d.arv)}</td>
                <td className="font-mono">{fmtMoney(d.asking_price)}</td>
                <td className="font-mono">{fmtMoney(d.repair_estimate)}</td>
                <td><StatusBadge status={d.status} /></td>
                <td className="text-sm" style={{ color: "var(--text-muted)" }}>{fmtDate(d.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TriageBadge({ triage }) {
  const verdict = triage.verdict || "needs_work";
  const colors = {
    likely_real: { bg: "rgba(74,222,128,0.12)", fg: "#4ade80", label: "✓ likely real" },
    needs_work: { bg: "rgba(245,166,35,0.12)", fg: "#f5a623", label: "~ needs work" },
    red_flag: { bg: "rgba(255,92,92,0.12)", fg: "#ff5c5c", label: "⚠ red flag" },
  };
  const c = colors[verdict] || colors.needs_work;
  return (
    <span
      className="ml-2 font-mono text-[10px] px-1.5 py-0.5 rounded"
      style={{ background: c.bg, color: c.fg }}
      title={triage.summary || ""}
      data-testid="triage-badge"
    >
      {c.label} · {triage.score}/10
    </span>
  );
}

function StatCard({ label, val, accent, small, pulse }) {
  return (
    <div className="surface-card p-4 relative overflow-hidden">
      {pulse && (
        <span
          aria-hidden
          style={{
            position: "absolute", top: 12, right: 12,
            width: 8, height: 8, borderRadius: 999,
            background: "var(--lime)",
            boxShadow: "0 0 12px rgba(197,226,106,0.8)",
            animation: "wuPulse 1.6s ease-in-out infinite",
          }}
        />
      )}
      <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className={`font-mono ${small ? "text-lg" : "text-2xl"} mt-1`} style={{ color: accent ? "var(--lime)" : "var(--text)" }}>
        {val ?? "—"}
      </div>
    </div>
  );
}
